import { useNavigate, Link } from 'react-router-dom'
import { useState, type KeyboardEvent } from 'react'
import { Eye, EyeOff, MessageCircle, ChevronRight } from 'lucide-react'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import { Card } from '@/common/ui/card'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/common/ui/dropdown-menu'
import { WalletType, ChatPlatform, OperatorGroupPermission } from '@/schema'
import { PlayerClearTurnoverModal } from '../PlayerClearTurnoverModal'
import { PlayerClearMainWalletTurnoverModal } from '../PlayerClearMainWalletTurnoverModal'
import { PlayerUpdateMainWalletBalanceModal } from '../PlayerUpdateMainWalletBalanceModal'
import { PlayerUpdateDiamondModal } from '../PlayerUpdateDiamondModal'
import { PlayerNoteColorModal } from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/PlayerNoteColorModal'
import { PlayerTagsCell } from '@/common/components/PlayerTagsCell'
import AssignPlayerTagsModal from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/AssignPlayerTagsModal'
import PlayerBanModal from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/PlayerBanModal'
import PlayerToggleDemoModal from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/PlayerToggleDemoModal'
import PlayerClearLockoutModal from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/PlayerClearLockoutModal'
import { PlayerEditFormModal } from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/PlayerEditFormModal'
import { PlayerResetPasswordModal } from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/PlayerResetPasswordModal'
import { PlayerDeleteButton } from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/PlayerDeleteButton'
import SetIsActive from '@/common/components/SetIsActive'
import useMutationUpdatePartner from '@/modules/manage-player/api/hooks/useMutationUpdatePartner'
import useQueryGetPlayerFullPhone from '@/modules/manage-player/api/hooks/useQueryGetPlayerFullPhone'
import useQueryGetOperatorMe from '@/modules/operator/api/hooks/useQueryGetMe'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import { useQueryClient } from '@tanstack/react-query'
import { PartnerQueryKeys, PlayerQueryKeys } from '@/modules/manage-player/keys'
import type { GetPlayerDetailResponse } from '@/modules/manage-player/type'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from '@/common/ui/dialog'
import { cn } from '@/common/lib/utils'
import PlayerCommissionInfo from '../PlayerCommissionInfo'
import PlayerReturnLossInfo from '../PlayerReturnLossInfo'

interface PlayerInfoProps {
	data: GetPlayerDetailResponse
}

const walletTypeLabels: Record<string, string> = {
	[WalletType.INVITE_FRIEND]: 'กิจกรรมชวนเพื่อน',
	[WalletType.RETURN_LOSS]: 'กิจกรรมคืนยอดเสีย',
	[WalletType.COMMISSION]: 'กิจกรรมคอมมิชชั่น',
	[WalletType.WHEEL]: 'กิจกรรมหมุนวงล้อ',
	[WalletType.DAILY_CHECK_IN]: 'กิจกรรมเช็คอินรายวัน',
	[WalletType.MAIN]: 'เงิน',
	[WalletType.PROMOTION]: 'เงินติดเงื่อนไข promotion',
	[WalletType.PARTNER]: 'พาร์ทเนอร์',
}

const platformLabels: Record<string, string> = {
	[ChatPlatform.LINE]: 'LINE',
}

export default function PlayerInfo({ data }: PlayerInfoProps) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { mutateAsync: updatePartner } = useMutationUpdatePartner()
	const { data: operatorMe } = useQueryGetOperatorMe()
	const { data: generalSetting } = useQueryGetGeneralSetting()
	const {
		data: fullPhoneData,
		refetch: fetchFullPhone,
		isFetching: isLoadingFullPhone,
	} = useQueryGetPlayerFullPhone(data.id)
	const [showFullPhone, setShowFullPhone] = useState(false)
	const [commissionDetailOpen, setCommissionDetailOpen] = useState(false)
	const [returnLossDetailOpen, setReturnLossDetailOpen] = useState(false)

	const isMaskEnabled = generalSetting?.enableMaskPhone ?? true

	const hasViewFullPhonePermission =
		operatorMe?.userGroup?.readPermission?.includes(
			OperatorGroupPermission.VIEW_FULL_PHONE,
		) ||
		operatorMe?.userGroup?.writePermission?.includes(
			OperatorGroupPermission.VIEW_FULL_PHONE,
		)

	const handleTogglePhone = async () => {
		if (showFullPhone) {
			setShowFullPhone(false)
			return
		}
		if (!fullPhoneData) {
			await fetchFullPhone()
		}
		setShowFullPhone(true)
	}

	// Get chat rooms from player data (if available)
	const chatRooms =
		(data as { chatRooms?: Array<{ id: string; platform: string }> })
			.chatRooms || []

	const handleGoToChat = (roomId: string) => {
		navigate(`/operator/chat/${roomId}`)
	}

	const handlePartnerToggle = async (value: boolean) => {
		if (!data.partnerAccount?.id) return
		await updatePartner({
			id: data.partnerAccount.id,
			payload: { isActive: value },
		})
		queryClient.invalidateQueries({
			queryKey: [PartnerQueryKeys.GetPartners],
		})
		queryClient.invalidateQueries({
			queryKey: [PartnerQueryKeys.GetPartnerById],
		})
		queryClient.invalidateQueries({
			queryKey: [PlayerQueryKeys.GetPlayerDetail, data.id],
		})
	}

	const currentWallet = data.wallets.find(
		(wallet) => wallet.id === data.currentWalletId,
	)

	const mainWallet = data.wallets.find(
		(wallet) => wallet.type === WalletType.MAIN,
	)

	const promotionWallet = data.wallets.find(
		(wallet) => wallet.type === WalletType.PROMOTION,
	)

	return (
		<div className="space-y-4 sm:space-y-6">
			{/* Header */}
			<div className="flex items-start justify-between gap-4">
				<div className="flex-1">
					<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
						ข้อมูลทั่วไป
					</h2>
					<p className="text-sm text-muted-foreground sm:text-base">
						ข้อมูลทั่วไปของสมาชิก
					</p>
				</div>
				<div className="flex items-center gap-2">
					{/* Chat Button */}
					{chatRooms.length > 0 && (
						<>
							{chatRooms.length === 1 ? (
								<Button
									variant="default"
									size="sm"
									className="gap-2"
									onClick={() => handleGoToChat(chatRooms[0].id)}
								>
									<MessageCircle className="h-4 w-4" />
									แชท
									{platformLabels[chatRooms[0].platform] ||
										chatRooms[0].platform}
								</Button>
							) : (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant="default" size="sm" className="gap-2">
											<MessageCircle className="h-4 w-4" />
											เปิดแชท
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										{chatRooms.map((room) => (
											<DropdownMenuItem
												key={room.id}
												onClick={() => handleGoToChat(room.id)}
											>
												<MessageCircle className="mr-2 h-4 w-4" />
												{platformLabels[room.platform] || room.platform}
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						</>
					)}

					{/* Player Actions */}
					<div className="flex items-center gap-1">
						<PlayerEditFormModal player={data as any} />
						<PlayerBanModal player={data as any} />
						<PlayerClearLockoutModal player={data as any} />
						<PlayerResetPasswordModal
							playerId={data.id}
							playerUsername={data.username || data.phone}
						/>
						<PlayerDeleteButton
							playerId={data.id}
							playerUsername={data.username || data.phone}
						/>
					</div>
				</div>
			</div>

			{/* Main Info Cards - Responsive Grid */}
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				{/* Player Info Card */}
				<Card className="space-y-4 p-4 sm:p-6">
					<div className="space-y-2">
						<h3 className="text-base font-semibold sm:text-lg">
							ข้อมูลผู้เล่น
						</h3>
						<div className="space-y-3 sm:grid sm:grid-cols-2 sm:gap-2 sm:space-y-0">
							{isMaskEnabled &&
								!!(data as Record<string, unknown>).username && (
									<div className="flex justify-between sm:contents">
										<div className="text-sm text-muted-foreground">
											ชื่อผู้ใช้
										</div>
										<div className="text-sm font-medium sm:font-normal">
											{String((data as Record<string, unknown>).username)}
										</div>
									</div>
								)}
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">
									เบอร์โทรศัพท์
								</div>
								<div className="flex items-center gap-2 text-sm font-medium sm:font-normal">
									<span>
										{showFullPhone && fullPhoneData
											? fullPhoneData.phone
											: data.phone}
									</span>
									{isMaskEnabled && hasViewFullPhonePermission && (
										<Button
											variant="ghost"
											size="sm"
											onClick={handleTogglePhone}
											disabled={isLoadingFullPhone}
											className="h-6 w-6 p-0 hover:bg-gray-100"
										>
											{showFullPhone ? (
												<EyeOff className="h-4 w-4 text-gray-500" />
											) : (
												<Eye className="h-4 w-4 text-gray-500" />
											)}
										</Button>
									)}
									{data.color && (
										<div
											className="h-5 w-5 shrink-0 rounded-full border-2 border-white shadow-sm"
											style={{ backgroundColor: data.color }}
											title={`สี: ${data.color}`}
										/>
									)}
									<PlayerNoteColorModal
										player={{
											id: data.id,
											phone: data.phone,
											username: data.username,
											note: data.note,
											color: data.color,
										}}
									/>
								</div>
							</div>
							{data.note && (
								<div className="flex justify-between sm:contents">
									<div className="text-sm text-muted-foreground">โน้ต</div>
									<div className="whitespace-pre-wrap text-sm font-medium sm:font-normal">
										{data.note}
									</div>
								</div>
							)}
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">ID</div>
								<div className="text-sm font-medium sm:font-normal">
									{data.id}
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">IP Address</div>
								<div className="text-sm font-medium sm:font-normal">
									{data.lastLoginIp || '-'}
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">สถานะ</div>
								<div>
									<Badge
										variant={data.isBanned ? 'destructive' : 'default'}
										className="text-xs"
									>
										{data.isBanned ? 'ถูกแบน' : 'ปกติ'}
									</Badge>
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">BypassLine</div>
								<div>
									<Badge
										variant={(data as any).bypassLine ? 'default' : 'secondary'}
										className="text-xs"
									>
										{(data as any).bypassLine ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
									</Badge>
								</div>
							</div>
							{data.isPartner && data.partnerAccount && (
								<div className="flex justify-between sm:contents">
									<div className="text-sm text-muted-foreground">
										เปิดพาร์ทเนอร์
									</div>
									<div className="flex items-center gap-2">
										<SetIsActive
											defaultChecked={data.partnerAccount.isActive}
											onMutate={handlePartnerToggle}
											successMessage={
												data.partnerAccount.isActive
													? 'ปิดใช้งานพาร์ทเนอร์สำเร็จ'
													: 'เปิดใช้งานพาร์ทเนอร์สำเร็จ'
											}
										/>
										<span className="text-xs text-muted-foreground">
											({data.partnerAccount.nickname})
										</span>
									</div>
								</div>
							)}
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">
									บัญชีทดลองของทาง auto
								</div>
								<div className="flex items-center">
									<PlayerToggleDemoModal
										player={{
											id: data.id,
											phone: data.phone,
											isAgentDemoAccount: data.isAgentDemoAccount,
										}}
									/>
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">
									ช่องทางที่รู้จัก
								</div>
								<div className="text-sm font-medium sm:font-normal">
									{data.channel?.name || '-'}
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">
									พาร์ทเนอร์ที่ชวน
								</div>
								<div className="text-sm font-medium sm:font-normal">
									{data.referringPartner ? (
										<Button
											asChild
											variant="link"
											className="h-auto p-0 text-sm"
										>
											<Link
												to={`/operator/manage-partner/${data.referringPartner.id}`}
											>
												{data.referringPartner.nickname}
											</Link>
										</Button>
									) : (
										'-'
									)}
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">แรงค์</div>
								<div className="text-sm font-medium sm:font-normal">
									{data.rank?.name || '-'}
								</div>
							</div>
							{data.isBanned && (
								<>
									<div className="flex justify-between sm:contents">
										<div className="text-sm text-muted-foreground">
											เหตุผลการแบน
										</div>
										<div className="text-sm font-medium sm:font-normal">
											{data.banReason}
										</div>
									</div>
								</>
							)}

							{data.bannedAt && (
								<>
									<div className="flex justify-between sm:contents">
										<div className="text-sm text-muted-foreground">
											วันที่แบน
										</div>
										<div className="text-sm font-medium sm:font-normal">
											{new Date(data.bannedAt).toLocaleDateString('th-TH', {
												year: 'numeric',
												month: 'long',
												day: 'numeric',
												hour: '2-digit',
												minute: '2-digit',
											})}
										</div>
									</div>
								</>
							)}

							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">
									เข้าร่วมเมื่อ
								</div>
								<div className="text-sm font-medium sm:font-normal">
									{new Date(data.createdAt).toLocaleDateString('th-TH', {
										year: 'numeric',
										month: 'long',
										day: 'numeric',
										hour: '2-digit',
										minute: '2-digit',
									})}
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">
									ใช้งานล่าสุด
								</div>
								<div className="text-sm font-medium sm:font-normal">
									{data.lastOnline
										? new Date(data.lastOnline).toLocaleDateString('th-TH', {
												year: 'numeric',
												month: '2-digit',
												day: '2-digit',
											})
										: '-'}
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">เล่นล่าสุด</div>
								<div className="text-sm font-medium sm:font-normal">
									{data.lastPlayAt
										? new Date(data.lastPlayAt).toLocaleDateString('th-TH', {
												year: 'numeric',
												month: 'long',
												day: 'numeric',
												hour: '2-digit',
												minute: '2-digit',
											})
										: '-'}
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">จำนวนเพชร</div>
								<div className="flex items-center gap-2">
									<div className="text-sm font-semibold text-amber-600">
										{data.diamondAmount.toLocaleString('th-TH') || 0} เพชร
									</div>
									<PlayerUpdateDiamondModal
										player={{
											id: data.id,
											phone: data.phone,
											diamondAmount: data.diamondAmount,
										}}
									/>
								</div>
							</div>
							{/* Go to Chat Button */}
							{chatRooms.length > 0 && (
								<div className="flex justify-between sm:contents">
									<div className="text-sm text-muted-foreground">แชท</div>
									<div>
										{chatRooms.length === 1 ? (
											<Button
												variant="outline"
												size="sm"
												className="h-7 gap-1"
												onClick={() => handleGoToChat(chatRooms[0].id)}
											>
												<MessageCircle className="h-3.5 w-3.5" />
												{platformLabels[chatRooms[0].platform] ||
													chatRooms[0].platform}
											</Button>
										) : (
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button
														variant="outline"
														size="sm"
														className="h-7 gap-1"
													>
														<MessageCircle className="h-3.5 w-3.5" />
														ไปที่แชท ({chatRooms.length})
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													{chatRooms.map((room) => (
														<DropdownMenuItem
															key={room.id}
															onClick={() => handleGoToChat(room.id)}
														>
															<MessageCircle className="mr-2 h-4 w-4" />
															{platformLabels[room.platform] || room.platform}
														</DropdownMenuItem>
													))}
												</DropdownMenuContent>
											</DropdownMenu>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				</Card>

				{/* Bank & Wallet Info Card */}
				<Card className="space-y-4 p-4 sm:p-6">
					{/* Bank Info */}
					<div className="space-y-2">
						<h3 className="text-base font-semibold sm:text-lg">ข้อมูลธนาคาร</h3>
						<div className="space-y-3 sm:grid sm:grid-cols-2 sm:gap-2 sm:space-y-0">
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">ธนาคาร</div>
								<div className="text-sm font-medium sm:font-normal">
									{data.bank.bankProvider}
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">ชื่อบัญชี</div>
								<div className="text-sm font-medium sm:font-normal">
									{data.bank.accountName}
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">เลขบัญชี</div>
								<div className="text-sm font-medium sm:font-normal">
									{data.bank.accountNumber}
								</div>
							</div>
						</div>
					</div>

					{/* Current Wallet Info */}
					<div className="space-y-2">
						<h3 className="text-base font-semibold sm:text-lg">
							ข้อมูลกระเป๋าเงิน
						</h3>
						<div className="space-y-3 sm:grid sm:grid-cols-2 sm:gap-2 sm:space-y-0">
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">
									ยอดเงินคงเหลือ
								</div>
								<div className="text-sm font-medium sm:font-normal">
									{(currentWallet?.balance || 0).toLocaleString('th-TH')} บาท
								</div>
							</div>
							<div className="flex justify-between sm:contents">
								<div className="text-sm text-muted-foreground">ประเภท</div>
								<div className="text-sm font-medium sm:font-normal">
									{currentWallet?.type
										? walletTypeLabels[currentWallet.type]
										: '-'}
								</div>
							</div>
							{currentWallet?.type === 'PROMOTION' &&
								currentWallet?.turnoverAmount !== undefined && (
									<>
										<div className="flex justify-between sm:contents">
											<div className="text-sm text-muted-foreground text-red-600">
												ติดยอดเทิร์น
											</div>
											<div className="text-sm font-medium text-red-600">
												{currentWallet.turnoverAmount.toLocaleString('th-TH')}{' '}
												บาท
											</div>
										</div>
									</>
								)}
							{currentWallet?.type === 'MAIN' &&
								currentWallet?.turnoverAmount !== undefined && (
									<>
										<div className="flex justify-between sm:contents">
											<div className="text-sm text-muted-foreground text-red-600">
												ติดยอดเทิร์น
											</div>
											<div className="text-sm font-medium text-red-600">
												{currentWallet.turnoverAmount.toLocaleString('th-TH')}{' '}
												บาท
											</div>
										</div>
									</>
								)}
						</div>
					</div>

					{/* Player Tags */}
					<div className="space-y-2">
						<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
							<h3 className="text-base font-semibold sm:text-lg">
								แท็กผู้เล่น
							</h3>
							<AssignPlayerTagsModal
								playerId={data.id}
								playerPhone={data.phone}
								currentTags={(data.tags || []) as never[]}
							/>
						</div>
						<div>
							<PlayerTagsCell tags={data.tags || []} />
						</div>
					</div>
				</Card>
			</div>

			{/* Wallet Section - Left: Balance, Right: Activities */}
			<div className="space-y-4">
				{/* Section Header */}
				<h3 className="text-base font-semibold sm:text-lg">ข้อมูลกระเป๋า</h3>

				{/* Two Column Layout: Balance Left, Activities Right */}
				<div className="grid gap-4 lg:grid-cols-2">
					{/* Left Side - Main Balance Cards */}
					<div className="space-y-4">
						<h4 className="text-sm font-medium text-muted-foreground">
							ยอดเงิน
						</h4>
						<div className="grid gap-4">
							{/* Main Wallet - Green theme */}
							{mainWallet && (
								<Card className="border-green-200 bg-green-50/50 p-4 dark:border-green-900 dark:bg-green-950/30">
									<div className="space-y-3">
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												<div className="h-3 w-3 rounded-full bg-green-500" />
												<h3 className="text-sm font-semibold text-green-700 dark:text-green-400">
													เงินคงเหลือ
												</h3>
											</div>
											<div className="text-xl font-bold text-green-700 dark:text-green-400">
												{mainWallet.balance.toLocaleString('th-TH')} บาท
											</div>
										</div>
										{/* Main Wallet Conditions - Show turnover if exists */}
										{mainWallet.turnoverAmount !== undefined &&
											mainWallet.turnoverAmount > 0 && (
												<div className="flex items-center justify-between">
													<div className="text-xs text-red-500">
														ติดยอดเทิร์น
													</div>
													<div className="text-sm font-medium text-red-500">
														{mainWallet.turnoverAmount.toLocaleString('th-TH')}{' '}
														บาท
													</div>
												</div>
											)}
										{/* Action Buttons - Always show */}
										<div className="flex flex-wrap gap-2 border-t border-green-200 pt-2 dark:border-green-800">
											<PlayerClearMainWalletTurnoverModal
												player={{
													id: data.id,
													phone: data.phone,
													username: data.username,
													mainWallet: {
														balance: mainWallet?.balance || 0,
														turnover: mainWallet?.turnoverAmount || 0,
													},
												}}
											/>
											<PlayerUpdateMainWalletBalanceModal
												player={{
													id: data.id,
													phone: data.phone,
													username: data.username,
													mainWallet: {
														balance: mainWallet?.balance || 0,
													},
												}}
											/>
										</div>
									</div>
								</Card>
							)}

							{/* Promotion Wallet - Orange/Amber theme */}
							{promotionWallet && (
								<Card className="border-amber-200 bg-amber-50/50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
									<div className="space-y-3">
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												<div className="h-3 w-3 rounded-full bg-amber-500" />
												<h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400">
													เงินติดโปร
												</h3>
											</div>
											<div className="text-xl font-bold text-amber-700 dark:text-amber-400">
												{promotionWallet.balance.toLocaleString('th-TH')} บาท
											</div>
										</div>
										{/* Promotion Conditions */}
										{(promotionWallet.turnoverAmount !== undefined ||
											promotionWallet.promotionId) && (
											<div className="space-y-2 border-t border-amber-200 pt-2 dark:border-amber-800">
												{promotionWallet.promotionId && (
													<div className="flex items-center justify-between">
														<div className="text-xs text-amber-600/70 dark:text-amber-400/70">
															โปรโมชั่น
														</div>
														<div className="truncate text-xs font-medium text-amber-700 dark:text-amber-400">
															{promotionWallet.promotion || '-'}
														</div>
													</div>
												)}
												{promotionWallet.turnoverAmount !== undefined &&
													promotionWallet.turnoverAmount > 0 && (
														<div className="flex items-center justify-between">
															<div className="text-xs text-red-500">
																ติดยอดเทิร์น
															</div>
															<div className="text-sm font-medium text-red-500">
																{promotionWallet.turnoverAmount.toLocaleString(
																	'th-TH',
																)}{' '}
																บาท
															</div>
														</div>
													)}
												{/* Clear Turnover Button for Promotion Wallet */}
												<PlayerClearTurnoverModal
													player={{
														id: data.id,
														phone: data.phone,
														username: data.username,
														promotionWallet: {
															balance: promotionWallet?.balance || 0,
															turnover: promotionWallet?.turnoverAmount || 0,
														},
													}}
												/>
											</div>
										)}
									</div>
								</Card>
							)}
						</div>
					</div>

					{/* Right Side - Activity Wallets */}
					<div className="space-y-4">
						<h4 className="text-sm font-medium text-muted-foreground">
							กระเป๋ากิจกรรม
						</h4>
						<div className="space-y-2">
							{data.wallets
								?.filter(
									(wallet) =>
										wallet.type !== WalletType.MAIN &&
										wallet.type !== WalletType.PROMOTION,
								)
								.map((wallet) => {
									const opensCommission = wallet.type === WalletType.COMMISSION
									const opensReturnLoss = wallet.type === WalletType.RETURN_LOSS
									const isInteractive = opensCommission || opensReturnLoss

									const handleOpenDetail = () => {
										if (opensCommission) setCommissionDetailOpen(true)
										if (opensReturnLoss) setReturnLossDetailOpen(true)
									}

									const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
										if (!isInteractive) return
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault()
											handleOpenDetail()
										}
									}

									return (
										<Card
											key={wallet.id}
											className={cn(
												'p-3',
												isInteractive &&
													'cursor-pointer transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
											)}
											role={isInteractive ? 'button' : undefined}
											tabIndex={isInteractive ? 0 : undefined}
											aria-label={
												isInteractive
													? `${walletTypeLabels[wallet.type] || wallet.type} — เปิดรายละเอียด`
													: undefined
											}
											onClick={isInteractive ? handleOpenDetail : undefined}
											onKeyDown={isInteractive ? handleKeyDown : undefined}
										>
											<div className="flex items-center justify-between gap-2">
												<h3 className="text-sm font-medium">
													{walletTypeLabels[wallet.type] || wallet.type}
												</h3>
												<div className="flex items-center gap-1">
													<div className="text-sm font-semibold">
														{wallet.balance.toLocaleString('th-TH')} บาท
													</div>
													{isInteractive && (
														<ChevronRight
															className="h-4 w-4 shrink-0 text-muted-foreground"
															aria-hidden
														/>
													)}
												</div>
											</div>
											{wallet.turnoverAmount !== undefined &&
												wallet.turnoverAmount > 0 && (
													<div className="mt-1 flex items-center justify-between text-xs">
														<span className="text-red-500">ติดเทิร์น</span>
														<span className="font-medium text-red-500">
															{wallet.turnoverAmount.toLocaleString('th-TH')}{' '}
															บาท
														</span>
													</div>
												)}
											{isInteractive && (
												<p className="mt-1.5 text-xs text-muted-foreground">
													คลิกเพื่อดูรายละเอียดตามประเภทเกม
												</p>
											)}
										</Card>
									)
								})}
						</div>
					</div>
				</div>
			</div>

			<Dialog
				open={commissionDetailOpen}
				onOpenChange={setCommissionDetailOpen}
			>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>คอมมิชชั่น (คืนยอดเล่น)</DialogTitle>
						<DialogDescription>
							ยอดคอมมิชชั่นเดือนนี้และอัตราตามประเภทเกม
						</DialogDescription>
					</DialogHeader>
					<PlayerCommissionInfo playerId={data.id} embedded />
				</DialogContent>
			</Dialog>

			<Dialog
				open={returnLossDetailOpen}
				onOpenChange={setReturnLossDetailOpen}
			>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>คืนยอดเสีย</DialogTitle>
						<DialogDescription>
							เงื่อนไข รอบคำนวณ และยอดตามประเภทเกม
						</DialogDescription>
					</DialogHeader>
					<PlayerReturnLossInfo playerId={data.id} embedded />
				</DialogContent>
			</Dialog>
		</div>
	)
}
